import { API_ENDPOINTS } from '../config/api.config';
import { apiGet, apiPost } from '../utils/apiClient';
import type {
  CriticalNotification,
  NotificationHistoryEvent,
} from '../types/criticalNotification.types';

interface ApiEnvelope<T> {
  success?: boolean;
  message?: string;
  count?: number;
  data?: T;
}

function buildQueryString(params: Record<string, string | number | undefined>) {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value == null || String(value).trim() === '') {
      return;
    }
    search.set(key, String(value));
  });
  const qs = search.toString();
  return qs ? `?${qs}` : '';
}

function mapNotification(raw: Record<string, unknown>): CriticalNotification {
  return {
    id: Number(raw.id),
    escalationInstanceId: Number(raw.escalationInstanceId ?? raw.escalation_instance_id),
    studyId: Number(raw.studyId ?? raw.study_id),
    patientId:
      raw.patientId != null
        ? Number(raw.patientId)
        : raw.patient_id != null
          ? Number(raw.patient_id)
          : null,
    facilityId: Number(raw.facilityId ?? raw.facility_id),
    orderingClinician: (raw.orderingClinician ?? raw.ordering_clinician) as string | null,
    criticalityBand: String(raw.criticalityBand ?? raw.criticality_band ?? ''),
    notificationStatus: String(raw.notificationStatus ?? raw.notification_status ?? ''),
    acknowledgementStatus: String(raw.acknowledgementStatus ?? raw.acknowledgement_status ?? ''),
    acknowledgedByUserId:
      raw.acknowledgedByUserId != null
        ? Number(raw.acknowledgedByUserId)
        : raw.acknowledged_by_user_id != null
          ? Number(raw.acknowledged_by_user_id)
          : null,
    acknowledgedByContactId:
      raw.acknowledgedByContactId != null
        ? Number(raw.acknowledgedByContactId)
        : raw.acknowledged_by_contact_id != null
          ? Number(raw.acknowledged_by_contact_id)
          : null,
    acknowledgedAt: (raw.acknowledgedAt ?? raw.acknowledged_at) as string | null,
    acknowledgementMethod: (raw.acknowledgementMethod ?? raw.acknowledgement_method) as
      | string
      | null,
    acknowledgementIp: (raw.acknowledgementIp ?? raw.acknowledgement_ip) as string | null,
    createdAt: (raw.createdAt ?? raw.created_at) as string | undefined,
    updatedAt: (raw.updatedAt ?? raw.updated_at) as string | null,
    study: (() => {
      const study = raw.study as Record<string, unknown> | null | undefined;
      if (!study) {
        return null;
      }
      return {
        id: study.id != null ? Number(study.id) : undefined,
        studyInstanceUid: (study.studyInstanceUid ?? study.study_instance_uid) as string | null,
        isCritical: Boolean(study.isCritical ?? study.is_critical),
        criticalityBand: (study.criticalityBand ?? study.criticality_band) as string | null,
        patientName: (study.patientName ?? study.patient_name) as string | null,
        risVisitId: (study.risVisitId ?? study.ris_visit_id) as string | null,
        patientUhid: (study.patientUhid ?? study.patient_uhid) as string | null,
        accessionNumber: (study.accessionNumber ?? study.accession_number) as string | null,
        testName: (study.testName ?? study.test_name) as string | null,
        modalityName: (study.modalityName ?? study.modality_name) as string | null,
        studyDisplayName: (study.studyDisplayName ?? study.study_display_name) as string | null,
        studyDescription: (study.studyDescription ?? study.study_description) as string | null,
      };
    })(),
    patient: raw.patient
      ? {
          id: (raw.patient as Record<string, unknown>).id as number | undefined,
          name: ((raw.patient as Record<string, unknown>).name ??
            (raw.patient as Record<string, unknown>).patient_name) as string | null,
          patientDicomId: ((raw.patient as Record<string, unknown>).patientDicomId ??
            (raw.patient as Record<string, unknown>).patient_dicom_id) as string | null,
          risVisitId: ((raw.patient as Record<string, unknown>).risVisitId ??
            (raw.patient as Record<string, unknown>).ris_visit_id) as string | null,
        }
      : null,
    escalationInstance: (raw.escalationInstance ?? raw.escalation_instance) as
      | CriticalNotification['escalationInstance']
      | null,
    acknowledgedByUser: (raw.acknowledgedByUser ?? raw.acknowledged_by_user) as
      | CriticalNotification['acknowledgedByUser']
      | null,
    acknowledgedByContact: (() => {
      const contact = (raw.acknowledgedByContact ?? raw.acknowledged_by_contact) as
        | Record<string, unknown>
        | null
        | undefined;
      if (!contact) {
        return null;
      }
      return {
        id: contact.id != null ? Number(contact.id) : undefined,
        name: (contact.name as string) ?? null,
        role: (contact.role as string) ?? null,
        mobileNumber: (contact.mobileNumber ?? contact.mobile_number) as string | null,
        department: (contact.department as string) ?? null,
      };
    })(),
  };
}

function mapHistoryEvent(raw: Record<string, unknown>): NotificationHistoryEvent {
  const rawId = raw.id;
  const id =
    typeof rawId === 'number'
      ? rawId
      : typeof rawId === 'string' && rawId.trim() !== '' && !Number.isNaN(Number(rawId))
        ? Number(rawId)
        : String(rawId ?? '');

  return {
    id,
    instanceId: Number(raw.instanceId ?? raw.instance_id ?? 0),
    eventType: String(raw.eventType ?? raw.event_type ?? raw.eventCategory ?? ''),
    levelNumber:
      raw.levelNumber != null
        ? Number(raw.levelNumber)
        : raw.level_number != null
          ? Number(raw.level_number)
          : null,
    contactId:
      raw.contactId != null
        ? Number(raw.contactId)
        : raw.contact_id != null
          ? Number(raw.contact_id)
          : null,
    role: (raw.role as string) ?? (raw.recipientRole as string) ?? null,
    message: (raw.message as string) ?? (raw.reason as string) ?? null,
    metadata: (raw.metadata as Record<string, unknown>) ?? null,
    createdAt: String(raw.createdAt ?? raw.created_at ?? raw.timestamp ?? ''),
    eventCategory: (raw.eventCategory ?? raw.event_category) as string | null,
    displayStatus: (raw.displayStatus ?? raw.display_status) as string | null,
    recipientName: (raw.recipientName ?? raw.recipient_name) as string | null,
    recipientRole: (raw.recipientRole ?? raw.recipient_role) as string | null,
    actor: (raw.actor as string) ?? null,
  };
}

export async function getCriticalNotification(
  id: number,
  facilityId: number
): Promise<{ success: boolean; data?: CriticalNotification; error?: string }> {
  try {
    const url = `${API_ENDPOINTS.CRITICAL_NOTIFICATIONS.DETAIL(id)}${buildQueryString({ facilityId })}`;
    const response = await apiGet<ApiEnvelope<Record<string, unknown>>>(url, { requireAuth: true });
    const payload = response.data;
    if (payload?.success === false || !payload?.data) {
      return { success: false, error: payload?.message || 'Failed to load notification' };
    }
    return { success: true, data: mapNotification(payload.data) };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to load notification',
    };
  }
}

export async function acknowledgeCriticalNotification(
  id: number,
  facilityId: number,
  acknowledgementMethod: 'API' | 'SMS' | 'IN_APP' = 'API'
): Promise<{ success: boolean; data?: CriticalNotification; error?: string }> {
  try {
    const url = API_ENDPOINTS.CRITICAL_NOTIFICATIONS.ACKNOWLEDGE(id);
    const response = await apiPost<ApiEnvelope<Record<string, unknown>>>(
      url,
      { facilityId, acknowledgementMethod },
      { requireAuth: true }
    );
    const payload = response.data;
    if (payload?.success === false || !payload?.data) {
      return { success: false, error: payload?.message || 'Failed to acknowledge notification' };
    }
    return { success: true, data: mapNotification(payload.data) };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to acknowledge notification',
    };
  }
}

export interface CriticalNotificationPublicStatus {
  criticalNotificationId: number;
  notificationStatus: string;
  acknowledgementStatus: string;
  acknowledgedAt: string | null;
}

function mapPublicStatus(raw: Record<string, unknown>): CriticalNotificationPublicStatus {
  return {
    criticalNotificationId: Number(raw.criticalNotificationId ?? raw.critical_notification_id ?? 0),
    notificationStatus: String(raw.notificationStatus ?? raw.notification_status ?? ''),
    acknowledgementStatus: String(raw.acknowledgementStatus ?? raw.acknowledgement_status ?? ''),
    acknowledgedAt: (raw.acknowledgedAt ?? raw.acknowledged_at ?? null) as string | null,
  };
}

/** Public (no-auth) status check used by the escalation contact acknowledgement page. */
export async function getCriticalNotificationPublicStatus(
  token: string
): Promise<{ success: boolean; data?: CriticalNotificationPublicStatus; error?: string }> {
  try {
    const url = `${API_ENDPOINTS.CRITICAL_NOTIFICATIONS.PUBLIC_STATUS}${buildQueryString({ token })}`;
    const response = await apiGet<ApiEnvelope<Record<string, unknown>> | Record<string, unknown>>(
      url,
      { requireAuth: false }
    );
    const payload = response.data as ApiEnvelope<Record<string, unknown>> & Record<string, unknown>;
    if (payload?.success === false) {
      return { success: false, error: payload?.message || 'Failed to load notification status' };
    }
    // Response may be wrapped in an envelope ({ data: {...} }) or returned directly
    const raw = (payload?.data ?? payload) as Record<string, unknown> | undefined;
    if (
      !raw ||
      (raw.acknowledgementStatus === undefined && raw.acknowledgement_status === undefined)
    ) {
      return { success: false, error: 'Failed to load notification status' };
    }
    return { success: true, data: mapPublicStatus(raw) };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to load notification status',
    };
  }
}

/** Public (no-auth) acknowledgement via capability token. */
export async function acknowledgeCriticalNotificationPublic(
  token: string,
  acknowledgementMethod: 'API' | 'SMS' | 'IN_APP' = 'IN_APP'
): Promise<{ success: boolean; error?: string }> {
  try {
    const response = await apiPost<ApiEnvelope<Record<string, unknown>>>(
      API_ENDPOINTS.CRITICAL_NOTIFICATIONS.PUBLIC_ACKNOWLEDGE,
      { token, acknowledgementMethod },
      { requireAuth: false }
    );
    const payload = response.data;
    if (payload?.success === false) {
      return { success: false, error: payload?.message || 'Failed to acknowledge notification' };
    }
    return { success: true };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to acknowledge notification',
    };
  }
}

async function fetchHistory(
  url: string
): Promise<{ success: boolean; data: NotificationHistoryEvent[]; count: number; error?: string }> {
  try {
    const response = await apiGet<ApiEnvelope<Record<string, unknown>[]>>(url, {
      requireAuth: true,
    });
    const payload = response.data;
    if (payload?.success === false) {
      return {
        success: false,
        data: [],
        count: 0,
        error: payload.message || 'Failed to load notification history',
      };
    }
    const rows = Array.isArray(payload?.data)
      ? payload.data.map(row => mapHistoryEvent(row as Record<string, unknown>))
      : [];
    return { success: true, data: rows, count: Number(payload?.count ?? rows.length) };
  } catch (error) {
    return {
      success: false,
      data: [],
      count: 0,
      error: error instanceof Error ? error.message : 'Failed to load notification history',
    };
  }
}

export async function getCriticalNotificationHistory(
  id: number,
  facilityId: number,
  params: { limit?: number; offset?: number } = {}
) {
  const url = `${API_ENDPOINTS.CRITICAL_NOTIFICATIONS.HISTORY(id)}${buildQueryString({
    facilityId,
    limit: params.limit,
    offset: params.offset,
  })}`;
  return fetchHistory(url);
}

export async function getCriticalNotificationReminderHistory(
  id: number,
  facilityId: number,
  params: { limit?: number; offset?: number } = {}
) {
  const url = `${API_ENDPOINTS.CRITICAL_NOTIFICATIONS.REMINDER_HISTORY(id)}${buildQueryString({
    facilityId,
    limit: params.limit,
    offset: params.offset,
  })}`;
  return fetchHistory(url);
}

export async function getCriticalNotificationEscalationHistory(
  id: number,
  facilityId: number,
  params: { limit?: number; offset?: number } = {}
) {
  const url = `${API_ENDPOINTS.CRITICAL_NOTIFICATIONS.ESCALATION_HISTORY(id)}${buildQueryString({
    facilityId,
    limit: params.limit,
    offset: params.offset,
  })}`;
  return fetchHistory(url);
}
